import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Button, Input, Select, Layout } from '../../../components';
import { useToast } from '../../../hooks/toast';
import { RoutesPaths } from '../../../routes';
import api from '../../../services/api';
import type { ParamsProps } from '../../../shared/interfaces/parameter';
import type { ScreenData } from '../../../shared/interfaces/screen';
import getValidationErrors from '../../../utils/getValidationErrors';
import icons from '../icons';
import urls from '../urls';
import { Container, FormContainer, FormFooter } from './styles';

interface EditProps {
  id: number;
  icone: string;
  titulo: string;
  subtitulo: string;
  url: string;
}

const Editar: React.FC = () => {
  const history = useHistory();
  const { id } = useParams<ParamsProps>();
  const { addToast } = useToast();
  let selectedIcon = useRef<any>();
  let selectedURL = useRef<any>();
  const formRef = useRef<FormHandles | null>(null);

  const [data, setData] = useState<EditProps>({
    id: 0,
    icone: '',
    titulo: '',
    subtitulo: '',
    url: '',
  });

  useEffect(() => {
    if (id !== undefined) {
      findScreen(id);
    }
  }, [id]);

  function updatedScreen(e: ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
  }

  const handleSubmit = useCallback(
    async (data: ScreenData) => {
      try {
        const schema = Yup.object().shape({
          icone: Yup.string().required('Este campo é obrigatório!'),
          titulo: Yup.string()
            .max(40, 'No máximo 40 caracteres')
            .required('Este campo é obrigatório!'),
          subtitulo: Yup.string()
            .max(40, 'No máximo 40 caracteres')
            .required('Este campo é obrigatório!'),
          url: Yup.string().required('Este campo é obrigatório!'),
        });
        data.icone = selectedIcon.current;
        data.url = selectedURL.current;

        await schema.validate(data, {
          abortEarly: false,
        });

        if (id !== undefined) {
          await api.put(`/tela/${id}`, data);
          addToast({
            type: 'success',
            title: 'Tela alterada com sucesso!',
            description: 'As informações da Tela foram alteradas.',
          });
        } else {
          await api.post('/tela', data);
        }
        setTimeout(() => {
          history.push(RoutesPaths.listScreen);
        }, 4000);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errorMessages = getValidationErrors(err);
          formRef.current?.setErrors(errorMessages);
          return;
        } else {
          console.error(err);
          addToast({
            type: 'error',
            title: 'Tela já cadastrada!',
            description:
              'As informações que foram inseridas, já estão presentes no sistema.',
          });
        }
      }
    },
    [history, id, addToast],
  );

  async function findScreen(id: string) {
    const response = await api.get(`/tela/id?id=${id}`);
    setData({
      id: response.data.id,
      icone: response.data.icone,
      titulo: response.data.titulo,
      subtitulo: response.data.subtitulo,
      url: response.data.url,
    });

    selectedIcon.current = response.data.icone;
    selectedURL.current = response.data.url;
  }

  return (
    <Layout title={'Editar Tela'} hideFloatingButton>
      <Container>
        <FormContainer>
          <Form onSubmit={handleSubmit} ref={formRef}>
            <Input
              type="text"
              name="titulo"
              value={data.titulo}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updatedScreen(e)}
              label="Título"
              loading={!data.titulo.length ? true : false}
            ></Input>

            <Input
              type="text"
              name="subtitulo"
              value={data.subtitulo}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updatedScreen(e)}
              label="Subtitulo"
              loading={!data.subtitulo.length ? true : false}
            ></Input>
            <Select
              name={'icone'}
              isMulti={false}
              options={icons}
              onChange={option => (selectedIcon.current = option.value)}
              placeholder="Selecione"
              label="Ícone"
              initialValues={data.icone}
              loading={!data.icone.length ? true : false}
            />
            <br></br>
            <Select
              name={'url'}
              isMulti={false}
              options={urls}
              onChange={option => (selectedURL.current = option.value)}
              placeholder="Selecione"
              label="URL"
              initialValues={data.url}
              loading={!data.url.length ? true : false}
            />

            <FormFooter>
              <Button
                variant="solid"
                style={{ marginTop: '1rem' }}
                type="submit"
                disabled={
                  !urls.length ||
                  !icons.length ||
                  !data.subtitulo ||
                  !data.titulo
                }
              >
                Atualizar
              </Button>
            </FormFooter>
          </Form>
        </FormContainer>
      </Container>
    </Layout>
  );
};

export default Editar;
